/**
 * "Ask about this estimate" — a reader, not an oracle.
 *
 * It answers from the estimate in front of you and refuses everything else.
 * That narrowness is the feature: the dangerous version of this panel is the
 * one that helpfully guesses three hours for a bumper, because that number
 * goes on an invoice and nobody remembers a chat box invented it.
 *
 * So the copy says what it is for, the suggestions steer toward questions it
 * can actually answer, and a refusal is shown as a normal answer rather than
 * an error — being told "I can't tell you that" is the product working.
 */

import { useEffect, useRef, useState } from 'react';
import { useRepo } from '../data/RepoProvider.js';

interface Turn {
  question: string;
  answer: string | null;
  error?: string;
  refused?: boolean;
}

/** Questions it can genuinely answer, so the first one is never a dead end. */
const SUGGESTIONS = [
  'What is still waiting for me?',
  'What could it not see?',
  'Why is the confidence low on anything?',
  'What is on the estimate so far?',
];

export function AskPanel({ reference, onClose }: {
  reference: string;
  onClose: () => void;
}) {
  const repo = useRepo();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [turns, busy]);

  async function send(question: string) {
    const text = question.trim();
    if (!text || busy) return;
    setQ('');
    setBusy(true);
    // The question goes up straight away, with the answer pending. Waiting on
    // a round trip to see your own words typed makes the panel feel broken.
    setTurns((t) => [...t, { question: text, answer: null }]);
    try {
      const res = await repo.askEstimate(reference, text);
      setTurns((t) => t.map((turn, i) => (i === t.length - 1
        ? { ...turn, answer: res.answer, refused: res.refused } : turn)));
    } catch (err) {
      setTurns((t) => t.map((turn, i) => (i === t.length - 1
        ? { ...turn, answer: null, error: err instanceof Error ? err.message : String(err) }
        : turn)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <aside className="askpanel" role="dialog" aria-label="Ask about this estimate">
      <header>
        <b>Ask about this estimate</b>
        <button className="btn small" onClick={onClose} aria-label="Close">✕</button>
      </header>

      <div className="askbody">
        {turns.length === 0 && (
          <div className="askintro">
            <p className="muted small">
              It reads <b>this estimate</b> and nothing else — what is proposed, what
              you have decided, what it could not see, and what is priced.
            </p>
            <p className="muted small">
              It will not guess hours or prices. It has no labour times and no price
              list; your catalog and rates decide those.
            </p>
            <div className="asksuggest">
              {SUGGESTIONS.map((sg) => (
                <button key={sg} className="btn small" disabled={busy}
                        onClick={() => void send(sg)}>{sg}</button>
              ))}
            </div>
          </div>
        )}

        {turns.map((t, i) => (
          <div key={i} className="askturn">
            <p className="askq">{t.question}</p>
            {t.answer !== null && (
              <p className={`aska${t.refused ? ' refused' : ''}`}>{t.answer}</p>
            )}
            {t.error && (
              <p className="aska err">
                {t.error}{' '}
                <button className="linkish" onClick={() => void send(t.question)}>
                  Try again
                </button>
              </p>
            )}
            {t.answer === null && !t.error && <p className="aska thinking">Reading…</p>}
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <form className="askform"
            onSubmit={(e) => { e.preventDefault(); void send(q); }}>
        <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Ask about this estimate…" maxLength={500}
               aria-label="Your question" disabled={busy} />
        <button className="btn primary" type="submit" disabled={busy || !q.trim()}>
          {busy ? '…' : 'Ask'}
        </button>
      </form>
    </aside>
  );
}
